import { Contract, ENewOrUpdatePrices, Premium } from '../../types';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useContractStandingData } from '../../hooks/useContractStandingData';
import { useState } from 'react';
import { Button } from '@portal_components/components/Button';
import { FormCard } from '@portal_components/components/FormCard';
import { FormCheckbox } from '@portal_components/components/FormCheckbox';
import { FormDropdown } from '@portal_components/components/FormDropdown';
import { FormSearchDropdown } from '@portal_components/components/FormSearchDropdown';
import { FormTextInput } from '@portal_components/components/FormTextInput';
import { IconButton } from '@portal_components/components/IconButton';

interface PremiumPricesTabProps {
  readonly: boolean;
}

export const PremiumPricesTab = ({ readonly }: PremiumPricesTabProps) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const {
    standingData: { standingDataSiteTypeList, standingDataPremiumJobList, standingDataServiceSubTypeMap }
  } = useContractStandingData();
  const { getValues, setValue, watch } = useFormContext<Contract>();

  const updateOrNewPrices = useWatch<Contract, 'updateOrNewPrices'>({ name: 'updateOrNewPrices' });

  const { fields, append, remove } = useFieldArray<Contract, 'premiumPrices'>({
    name: 'premiumPrices'
  });

  const handleRemovePremium = (index: number) => () => {
    if (getValues(`premiumPrices.${index}.conPremiumPriceId`)) {
      const markedForDeletion = getValues(`premiumPrices.${index}.markedForDeletion`);
      setValue(`premiumPrices.${index}.markedForDeletion`, !markedForDeletion);
      return;
    }
    remove(index);
  };

  const renderPremium = (_: Premium, index: number) => {
    const markedForDeletion = watch(`premiumPrices.${index}.markedForDeletion`);
    return (
      <div className="flex flex-col w-full pl-2" key={`ServiceStandardPrice-${index}`}>
        {markedForDeletion && <p className="text-feedback-orange text-sm">Premium price marked for deletion</p>}
        <div className="grid grid-cols-12 grid-rows-1 pb-2">
          <div className="col-span-3">
            <FormDropdown<Contract>
              title="Site Type"
              name={`premiumPrices.${index}.siteTypeId`}
              mandatory
              placeholder="Select Site Type..."
              elementsForDisplay={standingDataSiteTypeList}
              readonly={readonly || markedForDeletion}
            />
          </div>
          <div className="col-span-6">
            <FormSearchDropdown<Contract>
              title="Premium Description"
              name={`premiumPrices.${index}.premiumJobId`}
              placeholder="Select..."
              elementsForDisplay={standingDataPremiumJobList.map(job => {
                return { title: job.premiumJobDescription, value: job.premiumJobId };
              })}
              readonly={readonly || markedForDeletion}
              mandatory
            />
          </div>
          <div className="col-span-2">
            <FormTextInput<Contract> title="Annual Base Price (£)" name={`premiumPrices.${index}.annualBasePrice`} readonly={readonly || markedForDeletion} mandatory />
          </div>
          {!readonly && (
            <div className="col-start-12 col-span-1 flex flex-row justify-center items-center">
              <IconButton icon="/delete.svg" onClick={handleRemovePremium(index)} />
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleAddDefaultPremiums = () => {
    setErrorMessage(null);
    const [serviceList, premiumPrices] = getValues(['serviceList', 'premiumPrices']);
    if (!serviceList) {
      setErrorMessage('Please add a service before adding premium prices');
      return;
    }
    const serviceCodes = serviceList.reduce<string[]>((acc, service) => {
      if (!service.srvCodeId) return acc;
      const childSrvCodes = standingDataServiceSubTypeMap[service.srvCodeId];
      if (childSrvCodes) {
        return [...acc, ...childSrvCodes];
      }
      return [...acc, service.srvCodeId];
    }, []);

    if (!standingDataPremiumJobList) return;
    let addedPremiums = false;
    for (const sdPremium of standingDataPremiumJobList) {
      if (sdPremium.defaultPremium !== 'Y') continue; // only add default premiums
      const foundPremium = premiumPrices && premiumPrices.find(premium => premium.premiumJobId == sdPremium.premiumJobId);
      if (foundPremium) continue;
      const foundService = serviceCodes.find(service => service == sdPremium.srvCodeId);
      if (!foundService) continue;
      addedPremiums = true;
      append({
        siteTypeId: '',
        premiumJobId: sdPremium.premiumJobId,
        annualBasePrice: sdPremium.defaultPrice,
        chargingPrice: ''
      });
    }
    if (!addedPremiums) {
      setErrorMessage('Tnere are no default premium prices for the selected services');
    }
  };

  const handleAddPremium = () => {
    setErrorMessage(null);
    const serviceList = getValues('serviceList');
    if (!serviceList || serviceList.length < 1) {
      setErrorMessage('Please add a service before adding premium prices');
      return;
    }
    append({});
  };

  return (
    <div className="flex flex-col w-full">
      {updateOrNewPrices === ENewOrUpdatePrices.NEW && (
        <FormCard>
          <FormCheckbox title="Any changes made to these prices will remove future dated prices" name={'futureDatedPremiumPrices'} readonly={readonly} />
        </FormCard>
      )}
      <FormCard title="Premiums">
        {errorMessage && <div className="text-feedback-red mb-4">{errorMessage}</div>}
        {!readonly && (
          <div className="flex flex-row gap-2">
            <Button text="Add Premium" disabled={false} primary={false} onClick={handleAddPremium} />
            <Button text="Add Default Premiums" disabled={false} primary={false} onClick={handleAddDefaultPremiums} />
          </div>
        )}
        {fields.length > 0 && (
          <div className="grid grid-cols-12 grid-rows-1">
            <div className="col-span-3">
              <p className="pl-3 truncate">Site Type</p>
            </div>
            <div className="col-span-6">
              <p className="pl-3 truncate">Premium Description</p>
            </div>
            <div className="col-span-2">
              <p className="pl-3 truncate">Annual Base Price</p>
            </div>
          </div>
        )}
        {fields.map(renderPremium)}
      </FormCard>
    </div>
  );
};
